import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createReservationSchema,
  ReservationFormValues,
} from "../reservation-page.schema";
import type { UserData } from "../../../hooks/useUserData";
import type {
  DonationCampaign,
  MerchantTerm,
  Ticket,
  TicketPersonFormField,
} from "../../../types/reservation";
import { useReservationStore } from "../../../stores/useReservationStore";
import { DeliveryMethod } from "../../../common/enums/DeliveryMethod";
import { useReservationResources } from "./useReservationResources";
import { parsePhoneNumber } from "../utils/parsePhoneNumber";
import { isPhoneNumberStub } from "../../../utils/phoneNumber";
import { getProductTotalQty } from "../utils/productQuantities";

type UseReservationFormParams = {
  userData?: UserData | null;
  ticketPersonFormFields: TicketPersonFormField[];
  tickets: Ticket[];
  merchantTerms?: MerchantTerm[];
  donationCampaign?: DonationCampaign | null;
  isKokEvent?: boolean;
  producerId?: number | null;
};

function isFullPhoneNumber(phone: string | null | undefined) {
  return !!phone && !isPhoneNumberStub(phone.trim());
}

export function useReservationForm({
  userData = null,
  ticketPersonFormFields,
  tickets,
  merchantTerms = [],
  donationCampaign = null,
  isKokEvent = false,
  producerId = null,
}: UseReservationFormParams) {
  const r = useReservationResources();
  const products = useReservationStore((s) => s.products);
  const skipContactValidation =
    isFullPhoneNumber(userData?.phonenumber) &&
    !!userData?.email?.trim() &&
    !!userData?.firstName?.trim() &&
    !!userData?.lastName?.trim();

  const schema = useMemo(
    () =>
      r
        ? createReservationSchema(
            ticketPersonFormFields,
            tickets,
            skipContactValidation,
            donationCampaign,
            r,
            isKokEvent,
            producerId,
            merchantTerms,
          )
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ticketPersonFormFields.length,
      tickets.length,
      skipContactValidation,
      donationCampaign,
      r,
      isKokEvent,
      producerId,
      merchantTerms.length,
    ],
  );

  const methods = useForm<ReservationFormValues>({
    resolver: schema ? zodResolver(schema) : undefined,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    shouldFocusError: false,
    defaultValues: {
      contact: {
        firstName: "",
        lastName: "",
        email: "",
        mobileCountryCode: undefined,
        mobile: "",
      },
      shipping: {
        street: "",
        postcode: "",
        number: "",
        area: "",
        city: "",
        country: "GR",
      },
      billing: { type: "receipt" },
      requiresShipping: false,
      deliveryMethodId: "",
      shippingFee: 0,
      receiveUpdates: false,
      saveInfoToAccount: true,
      acceptTerms: false,
      donationAmount: 0,
      ticketPersonData: {},
      merchantTerms: {},
    },
  });

  // Set default delivery method based on cart contents, only if user hasn't selected yet.
  useEffect(() => {
    if (methods.getValues("deliveryMethodId") !== "") return;
    const productsInCart = products.filter((p) => getProductTotalQty(p) > 0);
    if (productsInCart.length === 0) return;
    const canShip = productsInCart.some((p) => p.canBeShipped);
    methods.setValue(
      "deliveryMethodId",
      canShip ? DeliveryMethod.Ship : DeliveryMethod.Collect,
    );
  }, [products, methods]);

  // Pre-populate contact fields once user data has loaded.
  useEffect(() => {
    if (!userData) return;
    if (userData.email) {
      methods.setValue("contact.email", userData.email, {
        shouldValidate: true,
      });
    }
    if (userData.firstName) {
      methods.setValue("contact.firstName", userData.firstName);
    }
    if (userData.lastName) {
      methods.setValue("contact.lastName", userData.lastName);
    }
    const parsedPhone = parsePhoneNumber(userData.phonenumber);
    if (parsedPhone) {
      if (parsedPhone.countryCode) {
        methods.setValue("contact.mobileCountryCode", parsedPhone.countryCode);
      }
      methods.setValue("contact.mobile", parsedPhone.mobile, {
        shouldValidate: true,
      });
    }
  }, [userData, methods]);

  return methods;
}
